import {useMutation} from '@apollo/client/react';
import {Question} from '@cp/model';
import {FLAG_QUESTION_AS_ERROR} from '../sharedQueries';

export function useFlagQuestionAsError() {
  const [flagQuestionAsErrorMutation, {loading, error}] = useMutation<
    {flagQuestionAsError: Question},
    {moduleId: string; questionId: string; statusNote?: string}
  >(FLAG_QUESTION_AS_ERROR);

  /**
   * Flag a question as possibly erroneous (user reports error)
   * @param moduleId
   * @param questionId
   * @param statusNote
   * @returns Question if successful, otherwise undefined
   */
  const flagQuestionAsError = async (moduleId: string, questionId: string, statusNote?: string) => {
    const result = await flagQuestionAsErrorMutation({
      variables: {moduleId, questionId, statusNote},
      update: (cache, {data}) => {
        if (data?.flagQuestionAsError) {
          // Update the question in the cache
          cache.modify({
            id: cache.identify(data.flagQuestionAsError),
            fields: {
              status: () => data.flagQuestionAsError.status,
              statusNote: () => data.flagQuestionAsError.statusNote,
            },
          });

          // Also update any session that contains this question
          // This ensures the currentQuestion in SessionPage gets updated
          cache.modify({
            fields: {
              userSessions: (existingSessions = []) =>
                existingSessions.map((session: any) => {
                  if (session.questions) {
                    const updatedQuestions = session.questions.map((question: any) => {
                      if (question.id === questionId) {
                        return {
                          ...question,
                          status: data.flagQuestionAsError.status,
                          statusNote: data.flagQuestionAsError.statusNote,
                        };
                      }
                      return question;
                    });
                    return {
                      ...session,
                      questions: updatedQuestions,
                    };
                  }
                  return session;
                }),
            },
          });
        }
      },
    });

    return result.data?.flagQuestionAsError;
  };

  return {
    flagQuestionAsError,
    loading,
    error,
  };
}
